import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { ApiError } from '@/lib/api.js';
import { personIdentitySender, personPhonesSender } from '@/components/account/record/factWriters.js';
import { getPersonIdentity } from '@/services/people.service.js';
import { getSmsPermission } from '@/services/sms-permission.service.js';
import SmsPermissionControl from '@/components/intake/SmsPermissionControl.jsx';
import { phoneTypeWord } from '@/lib/phone-type.js';
import { PHONE_LABELS, oldNumberChoices, keepOldAsBody } from '@/lib/phone-labels.js';

// THE PERSON EDIT FORM (people-create-door C4, ruling 1: a person's name, email
// and phone are edited in TRACON, never in CRM). It reads the person through the
// one read (GET /person/:contactId/identity: the store's answer in the door's own
// words) and posts ONLY through the factWriters sender (the one client path to
// PATCH /person/:contactId/identity), with only the fields that changed. After a
// save it shows the server's read-back, never the typed text; each warning is
// one toast.info. The second email shows only when the person's store is people
// (D5: zoho mode keeps today's single email and number). The type word beside
// the number is the server's (stated, or "Mobile (assumed)"). A name confirmed
// against the FAA registry answers 409 registry_confirmed: the form shows the
// registry name and asks the reason, sent as override_registry (AR13). The saved
// number carries the text permission read from the store; an edit never grants
// it (AR10: the door warns when the new main has none).
// OTHER NUMBERS (C6, ruling 2: the main is a mobile unless stated; more numbers
// carry a label). Only when the person's store is people (D5: zoho mode keeps
// one number and these doors answer 409 there): the main's type can be stated,
// a replaced main can be kept as a 2nd number (keep_old_as; over a copy or rule
// main the server keeps it unasked and "replace" sends false, Q57, read from the
// server's phone_main_class), and the list adds,
// relabels, retires (a reason required) and makes main through the factWriters
// numbers sender (the one client path to the C2 doors). Each number carries its
// own text permission, read from the store. A number action answers the person
// read back; what is typed in the fields above survives it.
// Guard: tests/person-edit-surfaces.test.js.

const KEYS = ['first_name', 'middle_name', 'last_name', 'preferred_name', 'email', 'secondary_email', 'phone'];

const draftOf = (p) => Object.fromEntries(KEYS.map((k) => [k, p?.[k] || '']));

// The sparse body: only what differs from the stored person. The second email
// is never sent unless the person's store is people (the door refuses it otherwise).
function changedFrom(person, draft, peopleStore) {
  const body = {};
  for (const k of KEYS) {
    if (k === 'secondary_email' && !peopleStore) continue;
    const v = draft[k].trim();
    if (v !== (person?.[k] || '')) body[k] = v;
  }
  return body;
}

// The main number's type as STATED ('' when the server says it is assumed).
const statedType = (p) => (p?.phone_type && !p.phone_type.assumed ? p.phone_type.type : '');
const labelWord = (l) => (l ? l[0].toUpperCase() + l.slice(1) : l);
const SELECT = 'min-h-[36px] rounded-[5px] border border-border bg-surface px-1.5 text-[12.5px] text-text disabled:opacity-50';

const errorText = (err) => (err instanceof ApiError && err.body?.error) || err?.message || 'Could not save';

function Field({ label, name, value, onChange, type = 'text' }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-text-muted">{label}</span>
      <Input name={name} type={type} value={value} onChange={(e) => onChange(name, e.target.value)} className="mt-1" autoComplete="off" />
    </label>
  );
}

export default function PersonIdentityForm({ contactId, onSaved, onCancel }) {
  const [person, setPerson] = useState(null);
  const [draft, setDraft] = useState(draftOf(null));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [registry, setRegistry] = useState(null);
  const [reason, setReason] = useState('');
  const [permission, setPermission] = useState(null);
  const [mainType, setMainType] = useState('');
  const [keepOldAs, setKeepOldAs] = useState('');
  const send = personIdentitySender({ door: 'person_identity', key: 'name', target: { contact_id: contactId } });
  const numbers = personPhonesSender({ door: 'person_identity', key: 'phone', target: { contact_id: contactId } });
  const peopleStore = person?.store === 'people';
  const phoneChanging = !!person?.phone && !!draft.phone.trim() && draft.phone.trim() !== person.phone;

  function showRead(p) {
    setPerson(p);
    setDraft(draftOf(p));
    setMainType(statedType(p));
    setKeepOldAs('');
    setRegistry(null);
    setReason('');
    setLoadError(null);
  }

  // A number action's read-back: the person is the server's; a field the broker
  // has typed into and not saved keeps what he typed.
  function rebase(p) {
    setDraft((d) => Object.fromEntries(KEYS.map((k) => [k, d[k] === (person?.[k] || '') ? (p?.[k] || '') : d[k]])));
    setMainType((t) => (t === statedType(person) ? statedType(p) : t));
    setPerson(p);
  }

  function numberAnswer(r, done) {
    if (r?.person) rebase(r.person);
    else { setPerson(null); setLoadError('Saved. TRACON could not read the person back to check it.'); }
    if (r?.changed === false) toast.info('Nothing was changed.');
    else toast.success(done);
    for (const w of Array.isArray(r?.warnings) ? r.warnings : []) toast.info(w.message || w.code);
    onSaved?.(r);
  }

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    getPersonIdentity(contactId)
      .then((r) => showRead(r?.person || null))
      .catch((err) => setLoadError(`Could not read this person: ${errorText(err)}`))
      .finally(() => setLoading(false));
  }, [contactId]);

  useEffect(() => { if (contactId) load(); }, [contactId, load]);

  const phone = person?.phone || null;
  const loadPermission = useCallback(() => {
    if (!phone) { setPermission(null); return; }
    getSmsPermission(phone)
      .then((r) => setPermission({ state: r?.state || 'unreadable', at: r?.permission?.granted_at || null }))
      .catch(() => setPermission({ state: 'unreadable', at: null }));
  }, [phone]);

  useEffect(() => { loadPermission(); }, [loadPermission]);

  const setField = (name, value) => setDraft((d) => ({ ...d, [name]: value }));

  async function save(e) {
    e?.preventDefault?.();
    if (!send) return;
    const body = changedFrom(person, draft, peopleStore);
    if (peopleStore && mainType && mainType !== statedType(person)) body.phone_type = mainType;
    if (peopleStore && phoneChanging) Object.assign(body, keepOldAsBody(keepOldAs));
    if (!Object.keys(body).length) { setError('Nothing changed.'); return; }
    if (registry) body.override_registry = { reason: reason.trim() };
    setSaving(true);
    setError(null);
    try {
      const r = await send(body);
      if (r?.person) showRead(r?.person);
      else { setPerson(null); setLoadError('Saved. TRACON could not read the person back to check it.'); }
      if (r?.unchanged) toast.info('Nothing was changed.');
      else toast.success('Saved');
      for (const w of Array.isArray(r?.warnings) ? r.warnings : []) toast.info(w.message || w.code);
      onSaved?.(r);
    } catch (err) {
      if (err instanceof ApiError && err.body?.code === 'registry_confirmed') setRegistry(err.body.registry || {});
      else setError(errorText(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading && !person) return <p className="text-sm text-text-muted">Loading…</p>;
  if (!person) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-danger">{loadError || 'Could not read this person.'}</p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={load}>Read again</Button>
          {onCancel ? <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Close</Button> : null}
        </div>
      </div>
    );
  }

  const registryName = registry ? [registry.first_name, registry.middle_name, registry.last_name].filter(Boolean).join(' ') : '';
  const typeWord = phoneTypeWord(person?.phone_type);

  return (
    <form onSubmit={save} className="space-y-3 rounded-card border border-border p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="First name" name="first_name" value={draft.first_name} onChange={setField} />
        <Field label="Middle name" name="middle_name" value={draft.middle_name} onChange={setField} />
        <Field label="Last name" name="last_name" value={draft.last_name} onChange={setField} />
      </div>
      <Field label="Preferred name" name="preferred_name" value={draft.preferred_name} onChange={setField} />
      <Field label="Email" name="email" type="email" value={draft.email} onChange={setField} />
      {peopleStore ? (
        <Field label="Second email" name="secondary_email" type="email" value={draft.secondary_email} onChange={setField} />
      ) : null}
      <Field label="Main phone" name="phone" type="tel" value={draft.phone} onChange={setField} />
      {peopleStore && phoneChanging ? (
        <select aria-label="The old main number" title="Keep the old main number as a 2nd number" value={keepOldAs} disabled={saving} onChange={(e) => setKeepOldAs(e.target.value)} className={SELECT}>
          {oldNumberChoices(person.phone_main_class).map((o) => <option key={o.value || 'blank'} value={o.value}>{o.label}</option>)}
        </select>
      ) : null}
      {person.phone ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
          <span>On file: {person.phone}</span>
          {typeWord ? <span className="text-text-dim">{typeWord}</span> : null}
          {peopleStore ? (
            <MainType value={mainType} disabled={saving} onChange={setMainType} />
          ) : null}
          {permission ? (
            <SmsPermissionControl phone={person.phone} state={permission.state} at={permission.at} crmContactId={contactId} onChanged={loadPermission} />
          ) : null}
        </div>
      ) : null}
      {peopleStore ? (
        <OtherNumbers person={person} contactId={contactId} numbers={numbers} onAnswer={numberAnswer} />
      ) : null}

      {registry ? (
        <div className="space-y-2 rounded-card border border-warning/40 bg-warning/10 p-3">
          <p className="text-xs text-text">
            This name is confirmed against the FAA airmen registry{registryName ? ` as ${registryName}` : ''}. To change it, give the reason.
          </p>
          <label className="block">
            <span className="text-xs font-medium text-text-muted">Reason (required)</span>
            <textarea
              name="override_reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text"
            />
          </label>
        </div>
      ) : null}

      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <div className="flex justify-end gap-2">
        {onCancel ? <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Close</Button> : null}
        <Button type="submit" size="sm" disabled={saving || !send || (registry && !reason.trim())}>
          {saving ? 'Saving…' : registry ? 'Save with reason' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

// What the main number is, stated by the broker (sent as phone_type with Save).
function MainType({ value, disabled, onChange }) {
  return (
    <select aria-label="What the main number is" title="What the main number is" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} className={SELECT}>
      <option value="">Type: not stated</option>
      {PHONE_LABELS.map((l) => <option key={l} value={l}>{`Main is ${l}`}</option>)}
    </select>
  );
}

// The person's other numbers (C6), each with its label, its own text permission,
// Make main and Retire (a reason required); and Add (a label required). Every
// action posts through the numbers sender and hands the answer to onAnswer.
function OtherNumbers({ person, contactId, numbers, onAnswer }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [newPhone, setNewPhone] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [retiring, setRetiring] = useState(null);
  const [retireReason, setRetireReason] = useState('');
  const [perms, setPerms] = useState({});
  const others = Array.isArray(person.other_phones) ? person.other_phones : [];
  const phonesKey = others.map((o) => o.phone).join('|');

  const loadPerm = useCallback((phone) => {
    getSmsPermission(phone)
      .then((r) => setPerms((p) => ({ ...p, [phone]: { state: r?.state || 'unreadable', at: r?.permission?.granted_at || null } })))
      .catch(() => setPerms((p) => ({ ...p, [phone]: { state: 'unreadable', at: null } })));
  }, []);

  useEffect(() => { phonesKey.split('|').filter(Boolean).forEach(loadPerm); }, [phonesKey, loadPerm]);

  async function run(key, call, done, after) {
    if (!numbers) return;
    setBusy(key);
    setError(null);
    try {
      const r = await call();
      after?.();
      onAnswer(r, done);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  const add = () => run('add', () => numbers.add({ phone: newPhone.trim(), label: newLabel }), 'Number added', () => { setNewPhone(''); setNewLabel(''); });
  const relabel = (o, label) => { if (label && label !== o.label) run(o.id, () => numbers.relabel(o.id, label), 'Label saved'); };
  const retire = (o) => run(o.id, () => numbers.retire(o.id, retireReason.trim()), 'Number retired', () => { setRetiring(null); setRetireReason(''); });
  const promote = (o) => run(o.id, () => numbers.promote(o.id), 'Main number changed');

  return (
    <div data-other-numbers="" className="space-y-2 rounded-card border border-border p-3">
      <p className="text-xs font-medium text-text-muted">Other numbers</p>
      {others.length ? (
        <ul className="space-y-2">
          {others.map((o) => (
            <li key={o.id} data-other-phone={o.id} className="space-y-1 text-xs text-text">
              <div className="flex flex-wrap items-center gap-2">
                <span>{o.phone}</span>
                <select aria-label={`What ${o.phone} is`} value={o.label || ''} disabled={!!busy} onChange={(e) => relabel(o, e.target.value)} className={SELECT}>
                  {o.label ? null : <option value="" disabled>label not stated</option>}
                  {PHONE_LABELS.map((l) => <option key={l} value={l}>{labelWord(l)}</option>)}
                </select>
                {perms[o.phone] ? (
                  <SmsPermissionControl phone={o.phone} state={perms[o.phone].state} at={perms[o.phone].at} crmContactId={contactId} onChanged={() => loadPerm(o.phone)} />
                ) : null}
                <Button type="button" variant="outline" size="xs" disabled={!!busy} onClick={() => promote(o)}>Make main</Button>
                {retiring === o.id ? null : (
                  <Button type="button" variant="ghost" size="xs" disabled={!!busy} onClick={() => { setRetiring(o.id); setRetireReason(''); }}>Retire</Button>
                )}
              </div>
              {retiring === o.id ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Input aria-label="Why retire this number" placeholder="Why (required)" value={retireReason} onChange={(e) => setRetireReason(e.target.value)} className="max-w-xs" autoComplete="off" />
                  <Button type="button" variant="outline" size="xs" disabled={!!busy || !retireReason.trim()} onClick={() => retire(o)}>Retire number</Button>
                  <Button type="button" variant="ghost" size="xs" disabled={!!busy} onClick={() => setRetiring(null)}>Keep</Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-text-dim">No other numbers.</p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Input aria-label="Another number" type="tel" placeholder="Another number" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} className="max-w-[12rem]" autoComplete="off" />
        <select aria-label="What the new number is" value={newLabel} disabled={!!busy} onChange={(e) => setNewLabel(e.target.value)} className={SELECT}>
          <option value="">Label (required)</option>
          {PHONE_LABELS.map((l) => <option key={l} value={l}>{labelWord(l)}</option>)}
        </select>
        <Button type="button" variant="outline" size="xs" disabled={!!busy || !newPhone.trim() || !newLabel} onClick={add}>Add number</Button>
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
    </div>
  );
}
